import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@/lib/ground";
import { inspectLogo, type LogoHalo } from "@/lib/logo-contrast";
import { cn } from "@/lib/utils";
import { TeamBadge } from "./team-badge";

/** A crest keeps its own colours; only low-contrast artwork gets a soft
 * silhouette halo. Used consistently across the tournament's award cards. */
export function ContrastTeamBadge({
  logo,
  color,
  short,
  surface = "card",
  className,
  loading,
}: {
  logo: string | undefined;
  color: string;
  short: string;
  surface?: "card" | "band";
  className: string;
  loading?: "lazy" | "eager" | undefined;
}) {
  const theme = useTheme();
  const host = useRef<HTMLSpanElement>(null);
  const key = `${theme}:${surface}:${logo ?? ""}`;
  const [result, setResult] = useState<{ key: string; halo: LogoHalo } | null>(null);
  const inspect = useCallback(() => {
    const node = host.current;
    const image = node?.querySelector("img");
    if (!node || !image?.complete || image.naturalWidth === 0) return;
    setResult({ key, halo: inspectLogo(image, node, surface) });
  }, [key, surface]);
  useEffect(inspect, [inspect]);
  const halo = result?.key === key ? result.halo : null;
  const tone = halo === "light" ? "var(--band-foreground)" : "var(--band)";
  return (
    <span
      ref={host}
      onLoadCapture={inspect}
      data-logo-halo={halo ?? "none"}
      className={cn("inline-flex shrink-0 items-center justify-center", className)}
      style={
        halo === null
          ? undefined
          : {
              filter: `drop-shadow(0 0 2px ${tone}) drop-shadow(0 0 2px ${tone}) drop-shadow(0 0 8px color-mix(in srgb, ${tone} 85%, transparent))`,
            }
      }
    >
      <TeamBadge
        logo={logo}
        color={color}
        short={short}
        loading={loading}
        className="size-full [font-size:inherit]"
      />
    </span>
  );
}
